import type { AgentCard, Gallery, OwnerView } from '@ralph/contracts'
import { DEFAULT_AGENT_ID } from '@ralph/contracts'

/**
 * The pure parts of the consumer surface.
 *
 * Everything here is a function of its arguments so it can be tested without a DOM: the
 * ordering rule the picker depends on, the fallback when a listing has no icon, and the
 * "this agent changed under you" comparison, which is the one piece of state in the chat
 * surface that is easy to get subtly wrong in both directions (never firing, or firing on
 * every render).
 */

/**
 * Initials for a listing with no icon.
 *
 * Two glyphs at most, because the tile is 32-40px and a third letter turns a monogram into
 * a smudge. Punctuation and digits are stripped before the split, so "2026 Travel Policy"
 * reads as "TP" rather than "2T" — a numeral in a monogram looks like a version number.
 */
export const monogram = (displayName: string): string => {
  const words = displayName
    .replace(/[^\p{L}\s]/gu, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 0)

  const first = words[0]
  if (!first) return '··'

  const second = words[1]
  if (second) return `${first.slice(0, 1)}${second.slice(0, 1)}`.toUpperCase()
  return first.slice(0, 2).toUpperCase()
}

/**
 * Every agent in the gallery a conversation can actually be started with, deduplicated.
 *
 * A card appears in several sections at once (featured AND all, typically), so the picker
 * has to flatten them. `state` is present only on the viewer's own listings, and anything
 * other than `published` there is a draft or a retired agent — offering those in a picker
 * would produce a conversation the server refuses, so they are filtered out rather than
 * shown disabled.
 *
 * The default agent leads, per AGM-004: every legacy conversation is pinned to it and a
 * fresh install has nothing else.
 */
export const conversableAgents = (gallery: Gallery): AgentCard[] => {
  const seen = new Set<string>()
  const ordered: AgentCard[] = []

  for (const card of [
    ...gallery.featured,
    ...gallery.sharedWithYourGroups,
    ...gallery.recent,
    ...gallery.all,
    ...gallery.mine
  ]) {
    if (seen.has(card.agentId)) continue
    if (card.state !== undefined && card.state !== 'published') continue
    seen.add(card.agentId)
    ordered.push(card)
  }

  const defaultIndex = ordered.findIndex((card) => card.agentId === DEFAULT_AGENT_ID)
  const defaultCard = defaultIndex > 0 ? ordered[defaultIndex] : undefined
  if (!defaultCard) return ordered

  return [defaultCard, ...ordered.filter((_, index) => index !== defaultIndex)]
}

/**
 * Which agent a surface should treat as chosen.
 *
 * `preferred` is what the person last asked for — router state handed over by the chat
 * header's picker, or the choice remembered from their previous visit — and it wins only if
 * it is still something they can converse with. A remembered agent that was retired,
 * unpublished or unshared since must degrade to the default rather than to a dangling id: the
 * pin is made at creation and cannot be moved (AGM-007), so starting a conversation against
 * an id the server will reject — or worse, silently swap — is the one outcome to avoid.
 *
 * Falls through to the first option, which `conversableAgents` has already ordered with the
 * deployment default at its head (AGM-004).
 */
export const pickAgent = (options: AgentCard[], preferred: string | null | undefined): AgentCard | null =>
  options.find((agent) => agent.agentId === preferred) ?? options[0] ?? null

/*
 * There was a `canSwitchAgent` here, and its removal is the point.
 *
 * It answered "is a picker worth drawing at all" with "only if there is something to switch
 * to", and both surfaces fell back to a plain label otherwise. The reasoning was that a menu
 * with one option is a menu that does nothing. The reasoning was wrong about which state is
 * the common one: a deployment ships with a single published agent, so the fallback was not an
 * edge case, it was the default — and it left the pill that carries an agent's identity tile
 * looking pressable and doing nothing on the two screens people use most.
 *
 * The picker now opens on any resolved listing. A one-item menu still says who answers, still
 * says the choice is permanent, and ends with the way to the gallery.
 */

/**
 * Whether the reader should be told the agent changed while this chat was open (AGM-046).
 *
 * The baseline is captured the first time the listing loads for a conversation; any later
 * value that differs means a republish landed since. It latches once shown — a notice that
 * reappears on every refetch reads as a stuck banner rather than news, and the reader has
 * already been told.
 */
export type UpdateNoticeState = { baseline: string | null; show: boolean }

export const initialUpdateNotice: UpdateNoticeState = { baseline: null, show: false }

export const nextUpdateNotice = (state: UpdateNoticeState, updatedAt: string | undefined): UpdateNoticeState => {
  if (!updatedAt) return state
  if (state.baseline === null) return { baseline: updatedAt, show: false }
  if (state.baseline === updatedAt) return state
  return { baseline: updatedAt, show: true }
}

export const dismissUpdateNotice = (state: UpdateNoticeState): UpdateNoticeState => ({ ...state, show: false })

/** The badges a card renders, in a fixed order so two cards never disagree about layout. */
export type CardBadge = { kind: 'knowledge'; count: number } | { kind: 'actions' } | { kind: 'memory' }

export const cardBadges = (badges: AgentCard['badges']): CardBadge[] => {
  const list: CardBadge[] = []
  if (badges.knowledgeBases > 0) list.push({ kind: 'knowledge', count: badges.knowledgeBases })
  if (badges.actions) list.push({ kind: 'actions' })
  if (badges.memory) list.push({ kind: 'memory' })
  return list
}

/**
 * Who an agent belongs to, as a name (PRD-26 D-19): a person's name, or a group's.
 *
 * For the places a name has to stand alone — an accessible label, a sentence. Wherever there is
 * room, a group owner is drawn with its avatar (`OwnerLabel`), because a group's name on its own
 * reads like a person's (GRP-102).
 */
export const ownerName = (owner: OwnerView): string => (owner.kind === 'user' ? owner.name : owner.group.name)
